import type { LiquidCoolingCduConfigurationsSaveHandle } from './subsections/liquidCoolingCduConfigurationsSaveHandle';
import type { SelectedFile } from '../../../components/forms/FileUpload';
import type { RepeatableInstanceSaveHandle } from '../../../components/repeatableSections/repeatableInstanceSaveHandle';
import type { SavedAttachment } from '../../../services/attachmentService';
import type { Request } from '../../../types';
import type { LiquidCoolingCduConfiguration } from '../../../types/liquidCoolingCdu';

export interface LiquidCoolingInitialProps {
  formData: Record<string, unknown>;
  updateFormData: (
    field: string,
    value:
      | string
      | number
      | boolean
      | LiquidCoolingCduConfiguration[]
      | undefined,
  ) => void;
  selectedFiles?: Record<string, SelectedFile[]>;
  onFilesSelected: (section: string, files: SelectedFile[]) => void;
  offlineMode?: boolean;
  requestId?: string;
  requestStage?: string;
  disabled?: boolean;
  attachmentsBySection?: Record<string, SavedAttachment[]>;
  onAttachFiles?: (sectionId: string) => void;
  onDeleteAttachment?: (attachmentId: number) => void;
  isEditing?: boolean;
  showFullDetails?: boolean;
  registerLiquidCoolingCduSaveHandle?: (
    handle: LiquidCoolingCduConfigurationsSaveHandle | null,
  ) => void;
  onLiquidCoolingCduEditorActiveChange?: (active: boolean) => void;
}

export interface LiquidCoolingPersistedProps {
  request: Request;
  isEditing: boolean;
  editedData: Record<string, unknown>;
  updateEditedData: (
    field: string,
    value: string | number | boolean | undefined,
  ) => void;
  showFullDetails?: boolean;
  attachmentsBySection?: Record<string, SavedAttachment[]>;
  selectedFiles?: Record<string, SelectedFile[]>;
  onFilesSelected?: (section: string, files: SelectedFile[]) => void;
  onRefetchAttachments?: () => void;
  showViewToggle?: boolean;
  isAdvancedView?: boolean;
  onToggleView?: () => void;
  offlineMode?: boolean;
  disabled?: boolean;
  onCaptionChange?: (attachmentId: number, caption: string) => void;
  pendingCaptionEdits?: Record<number, string>;
  onAttachFiles?: (sectionId: string) => void;
  onDeleteAttachment?: (attachmentId: number) => void;
  /**
   * Request Detail engine wiring (#997 Story 6.8). The engine section registers
   * its save handle with the screen's `useLiquidCoolingCduEngineSaveBridge` and
   * writes the live session list into `editedData.liquidCoolingCduConfigurations`
   * via `applyEditedDataPatch` so it survives step navigation. `updateEditedData`
   * is retained for the request-level shared field `lcLiquidToChipDesired`.
   */
  registerInstanceSaveHandle?: (
    handle: RepeatableInstanceSaveHandle | null,
  ) => void;
  applyEditedDataPatch?: (patch: Record<string, unknown>) => void;
  /** True when `editedData.liquidCoolingCduConfigurations` was touched this edit session. */
  liquidCoolingCduConfigurationsDirty?: boolean;
  onLiquidCoolingCduEditorActiveChange?: (active: boolean) => void;
}

export type LiquidCoolingProps =
  | LiquidCoolingInitialProps
  | LiquidCoolingPersistedProps;

export function isLiquidCoolingPersistedProps(
  p: LiquidCoolingProps,
): p is LiquidCoolingPersistedProps {
  return 'request' in p && (p as LiquidCoolingPersistedProps).request != null;
}
